import {
  useSettings,
  useUpdateSettings,
} from '@sparklayer-io/spark-dash-core/context/settings-context';
import { SettingToggleRow } from '@sparklayer-io/spark-dash-pages/components/setting-toggle-row';
import { useHandlers } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { PaginationOptions } from '@sparklayer-io/spark-dash-ui/utils/pagination';
import { Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';

const COMPANY_SYNC_I18N =
  'features:platform.pages.platform_page.configurations.company_sync' satisfies I18nPrefix;

/**
 * Toggles Shopify company sync. Enabling replaces any companies already in SparkLayer with the
 * Shopify structure, so the merchant must confirm that loss first; disabling keeps the synced
 * structure but stops syncing it, which is confirmed too.
 */
export function CompanySyncRow() {
  const { apis, notificationHandler } = useHandlers();
  const settings = useSettings();
  const { mutate } = useUpdateSettings();

  const [checking, setChecking] = useState(false);
  const [confirm, setConfirm] = useState<'enable' | 'disable' | null>(null);

  function save(value: boolean) {
    setConfirm(null);
    mutate({ key: 'companySyncEnabled', value });
  }

  async function handleEnable() {
    setChecking(true);
    try {
      const { pagination } = await apis.companies.list({}, new PaginationOptions({ pageSize: 1 }));
      if (pagination.total > 0) {
        setConfirm('enable');
      } else {
        save(true);
      }
    } catch {
      notificationHandler.error();
    } finally {
      setChecking(false);
    }
  }

  return (
    <>
      <SettingToggleRow
        label={t(`${COMPANY_SYNC_I18N}.label`)}
        description={t(`${COMPANY_SYNC_I18N}.description`)}
        checked={settings.companySyncEnabled ?? false}
        disabled={checking}
        onChange={(checked) => (checked ? void handleEnable() : setConfirm('disable'))}
      />

      <Modal
        isOpen={confirm === 'enable'}
        onClose={() => setConfirm(null)}
        primaryAction={{
          text: t(`${COMPANY_SYNC_I18N}.enable_modal.confirm`),
          onClick: () => save(true),
          danger: true,
        }}
        secondaryAction={{ text: t('buttons:cancel'), onClick: () => setConfirm(null) }}
      >
        <Modal.Header>{t(`${COMPANY_SYNC_I18N}.enable_modal.title`)}</Modal.Header>
        <Modal.Body>
          <Text variant="secondary">{t(`${COMPANY_SYNC_I18N}.enable_modal.message`)}</Text>
        </Modal.Body>
      </Modal>

      <Modal
        isOpen={confirm === 'disable'}
        onClose={() => setConfirm(null)}
        primaryAction={{
          text: t(`${COMPANY_SYNC_I18N}.disable_modal.confirm`),
          onClick: () => save(false),
        }}
        secondaryAction={{ text: t('buttons:cancel'), onClick: () => setConfirm(null) }}
      >
        <Modal.Header>{t(`${COMPANY_SYNC_I18N}.disable_modal.title`)}</Modal.Header>
        <Modal.Body>
          <Text variant="secondary">{t(`${COMPANY_SYNC_I18N}.disable_modal.message`)}</Text>
        </Modal.Body>
      </Modal>
    </>
  );
}
